import { Center, Group, Loader, Paper, Text } from '@mantine/core';
import { useState } from 'react';
import { Navigate, useNavigate } from 'react-router';
import useCurrentUser from 'src/admin/users/hooks/useCurrentUser';
import { useCurrentClient } from 'src/clients/context/CurrentClientProvider';
import { isSelectableAdvisor, useAdvisors } from 'src/clients/hooks/useAdvisors';
import { useDeleteFamily } from 'src/clients/hooks/useDeleteFamily';
import { useDeleteFamilyWithMembers } from 'src/clients/hooks/useDeleteFamilyWithMembers';
import { useFamily } from 'src/clients/hooks/useFamily';
import { useReassignFamilyAdvisor } from 'src/clients/hooks/useReassignFamilyAdvisor';
import { DropdownButton, DropdownButtonItem } from 'src/design-system/DropdownButton';
import { useGlobalConfirmation } from 'src/design-system/GlobalConfirmationProvider';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { Logger } from 'src/utils/Logger';
import { formatName, NameFormat } from 'src/utils/nameFormatting';
import DisbandFamilyNotes from './DisbandFamilyNotes';
import FamilyConfirmModal from './FamilyConfirmModal';
import { FamilyEmptyState } from './FamilyEmptyState';
import { FamilyMembersTable } from './FamilyMembersTable';

export function FamilyProfileContent() {
  const { client, isLoading } = useCurrentClient();

  if (isLoading) {
    return (
      <Center flex={1}>
        <Loader />
      </Center>
    );
  }

  if (!client) {
    return null;
  }

  if (client.familyId) {
    return <Navigate to={`/family/${client.familyId}/profile?member=${client.id}`} replace />;
  }

  return (
    <Center flex={1}>
      <FamilyEmptyState client={client} />
    </Center>
  );
}

export function FamilyProfilePanel({ familyId }: { familyId: string }) {
  const { family, isLoading: isFamilyLoading } = useFamily(familyId);
  const { advisors } = useAdvisors();
  const {
    reassignFamilyAdvisor,
    isLoading: isReassigning,
    error: reassignError,
    reset: resetReassign,
  } = useReassignFamilyAdvisor();
  const { isAdvisor } = useCurrentUser();
  const {
    deleteFamily,
    isLoading: isDisbanding,
    error: disbandError,
    reset: resetDisband,
  } = useDeleteFamily();
  const {
    deleteFamilyWithMembers,
    isLoading: isDeletingMembers,
    error: deleteMembersError,
    reset: resetDeleteMembers,
  } = useDeleteFamilyWithMembers();
  const navigate = useNavigate();
  const { showConfirmation } = useGlobalConfirmation();
  const [pendingRemoval, setPendingRemoval] = useState<'disband' | 'delete' | null>(null);
  const [pendingAdvisorId, setPendingAdvisorId] = useState<string | null>(null);

  if (isFamilyLoading) {
    return (
      <Paper variant="content">
        <Center py="xxl">
          <Loader />
        </Center>
      </Paper>
    );
  }

  if (!family) {
    return (
      <Paper variant="content">
        <Text c="gray.6">This family could not be found.</Text>
      </Paper>
    );
  }

  function requestAdvisorChange(advisorId: string) {
    resetReassign();
    setPendingAdvisorId(advisorId);
  }

  async function confirmAdvisorChange() {
    if (!pendingAdvisorId) return;
    try {
      await reassignFamilyAdvisor(familyId, pendingAdvisorId);
      setPendingAdvisorId(null);
      showConfirmation('Adviser changed');
    } catch (e) {
      Logger.error('Failed to change family advisor', e);
    }
  }

  function requestDisband() {
    resetDisband();
    setPendingRemoval('disband');
  }

  function requestDelete() {
    resetDeleteMembers();
    setPendingRemoval('delete');
  }

  async function confirmRemoval() {
    if (!family) return;
    const isDelete = pendingRemoval === 'delete';
    try {
      // The delete endpoint removes the family and every member in one
      // transaction, so a failure rolls back instead of orphaning members.
      await (isDelete ? deleteFamilyWithMembers(familyId) : deleteFamily(familyId));
      setPendingRemoval(null);
      showConfirmation(isDelete ? 'Family deleted' : 'Family disbanded');
      navigate('/clients/families');
    } catch (e) {
      Logger.error('Failed to remove family', e);
    }
  }

  const familyMenuItems: DropdownButtonItem[] = [
    { label: 'Add member', onClick: () => navigate(`/family/${familyId}/add-member`) },
    { label: 'Manage roles', onClick: () => navigate(`/family/${familyId}/edit`) },
    { label: 'Change family name', onClick: () => navigate(`/family/${familyId}/edit?mode=name`) },
    { label: 'Disband family', onClick: requestDisband },
    { label: 'Delete family and all members', onClick: requestDelete },
  ];

  const currentAdvisor = advisors.find(a => a.id === family.advisorUserId);
  const currentAdvisorName = currentAdvisor
    ? formatName(currentAdvisor.firstName, currentAdvisor.lastName, NameFormat.NATURAL)
    : 'Unassigned';
  const advisorItems: DropdownButtonItem[] = advisors
    .filter(isSelectableAdvisor)
    .filter(a => a.id !== family.advisorUserId)
    .map(a => ({
      label: formatName(a.firstName, a.lastName, NameFormat.NATURAL),
      onClick: () => requestAdvisorChange(a.id),
    }));

  const pendingAdvisor = advisors.find(a => a.id === pendingAdvisorId);
  const pendingAdvisorName = pendingAdvisor
    ? formatName(pendingAdvisor.firstName, pendingAdvisor.lastName, NameFormat.NATURAL)
    : 'the new adviser';

  return (
    <Paper variant="content">
      <Group align="center" mb="lg" gap={16}>
        <Group gap="xs" align="center">
          <DropdownButton label="Edit family" items={familyMenuItems} ariaLabel="Family actions" />
        </Group>
        {!isAdvisor && (
          <Group gap={4} align="center">
            <Text
              c="gray.9"
              size="xs"
              fw={FONT_WEIGHTS.semibold}
              style={{ paddingTop: 3, paddingBottom: 3 }}
            >
              Adviser:
            </Text>
            <DropdownButton
              label={currentAdvisorName}
              items={advisorItems}
              ariaLabel="Change adviser"
            />
          </Group>
        )}
      </Group>
      <FamilyMembersTable familyId={familyId} />
      <FamilyConfirmModal
        opened={pendingRemoval !== null}
        onClose={() => setPendingRemoval(null)}
        onConfirm={confirmRemoval}
        title={
          pendingRemoval === 'delete' ? 'Delete family and all members?' : 'Disband this family?'
        }
        description={
          pendingRemoval === 'delete'
            ? `Permanently deletes the ${family.name} family and all member profiles, including their financial data, documents and meeting history. This action cannot be undone.`
            : 'Keeps members’ records intact, just removes all members from this family group.'
        }
        extra={pendingRemoval === 'disband' ? <DisbandFamilyNotes /> : undefined}
        confirmLabel={pendingRemoval === 'delete' ? 'Delete' : 'Disband family'}
        error={disbandError ?? deleteMembersError}
        isLoading={isDisbanding || isDeletingMembers}
      />
      <FamilyConfirmModal
        opened={pendingAdvisorId !== null}
        onClose={() => setPendingAdvisorId(null)}
        onConfirm={confirmAdvisorChange}
        title={`Change adviser for ${family.name} family?`}
        description={`You're reassigning this family from ${currentAdvisorName} to ${pendingAdvisorName}. They'll gain full access to the family's profile, documents and meeting history.`}
        confirmLabel="Change adviser"
        cancelLabel="Keep current adviser"
        error={
          reassignError ? { message: 'Could not change the adviser. Please try again.' } : null
        }
        isLoading={isReassigning}
      />
    </Paper>
  );
}
